<template>
  <v-dialog v-model="showDeviceGroupDialog" max-width="900" persistent>
    <v-card>
      <v-toolbar outlined>
        <span class="text-h6">Device Groups Management</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4">
        <!-- Action Bar -->
        <v-row align="center" class="mb-2">
          <v-col cols="6">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search Groups"
              dense
              hide-details
              clearable
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="6" class="text-right">
            <v-btn color="primary" @click="openCreateDialog">
              <v-icon left>mdi-plus</v-icon>
              New Group
            </v-btn>
          </v-col>
        </v-row>

        <!-- Groups Table -->
        <v-data-table
          :headers="headers"
          :items="groups"
          :search="search"
          :loading="loading"
          class="elevation-1"
          item-key="id"
          :expanded.sync="expanded"
          show-expand
          single-expand
        >
          <!-- Name Column -->
          <template v-slot:item.name="{ item }">
            <strong>{{ item.name }}</strong>
          </template>

          <!-- Devices Count Column -->
          <template v-slot:item.devices="{ item }">
            <v-chip small color="primary" text-color="white">
              {{ item.categories.length }} device{{ item.categories.length !== 1 ? 's' : '' }}
            </v-chip>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="primary" @click="openEditDialog(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small color="red" @click="deleteGroup(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>

          <!-- Expanded Row (devices list) -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-4">
              <div v-if="item.categories.length === 0" class="text--secondary">
                No devices assigned to this group.
              </div>
              <v-chip
                v-else
                v-for="cat in item.categories"
                :key="cat.id"
                class="ma-1"
                small
                outlined
              >
                {{ cat.name }}
                <span class="ml-1 text--secondary">(ID: {{ cat.chipset_id }})</span>
              </v-chip>
            </td>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- Create / Edit Dialog -->
      <v-dialog v-model="showFormDialog" max-width="600" persistent>
        <v-card>
          <v-toolbar outlined>
            <span class="text-h6">{{ isEditMode ? 'Edit' : 'New' }} Device Group</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="closeFormDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pt-4">
            <v-form ref="groupForm" v-model="formValid" lazy-validation>
              <v-text-field
                v-model="formData.name"
                label="Group Name"
                :rules="nameRules"
                outlined
                dense
                required
                prepend-inner-icon="mdi-label"
              ></v-text-field>

              <v-autocomplete
                v-model="formData.category_ids"
                :items="allDevices"
                item-text="displayName"
                item-value="id"
                label="Assign Devices"
                multiple
                outlined
                dense
                chips
                small-chips
                deletable-chips
                clearable
                prepend-inner-icon="mdi-devices"
                :loading="loadingDevices"
              >
                <template v-slot:item="{ item, on, attrs }">
                  <v-list-item v-on="on" v-bind="attrs">
                    <v-list-item-action>
                      <v-checkbox :value="formData.category_ids.includes(item.id)" :input-value="formData.category_ids.includes(item.id)"></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle>Chipset ID: {{ item.chipset_id }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-form>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn text @click="closeFormDialog">Cancel</v-btn>
            <v-btn
              color="primary"
              @click="saveGroup"
              :disabled="!formValid || savingGroup"
              :loading="savingGroup"
            >
              {{ isEditMode ? 'Update' : 'Create' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'
export default {
  data () {
    return {
      search: '',
      loading: false,
      groups: [],
      expanded: [],
      allDevices: [],
      loadingDevices: false,
      showFormDialog: false,
      isEditMode: false,
      editingGroupId: null,
      formValid: true,
      savingGroup: false,
      formData: {
        name: '',
        category_ids: []
      },
      nameRules: [
        (v) => !!v || 'Group name is required',
        (v) => (v && v.length >= 2) || 'Group name must be at least 2 characters'
      ],
      headers: [
        { text: 'Group Name', value: 'name', sortable: true },
        { text: 'Devices', value: 'devices', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'end' },
        { text: '', value: 'data-table-expand' }
      ]
    }
  },
  props: {
    showDeviceGroupDialog: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    showDeviceGroupDialog (val) {
      if (val) {
        this.fetchGroups()
        this.fetchDevices()
      }
    }
  },
  methods: {
    async fetchGroups () {
      this.loading = true
      try {
        const res = await apiService.get('admin/device-groups')
        this.groups = res.data.groups || []
      } catch (err) {
        console.error('Failed to fetch device groups:', err)
        Swal.fire('Error', 'Failed to load device groups.', 'error')
      } finally {
        this.loading = false
      }
    },

    async fetchDevices () {
      this.loadingDevices = true
      try {
        const res = await apiService.get('admin/get-docker-sources')
        const devices = res.data.data.categories || []
        this.allDevices = devices.map(d => ({
          ...d,
          displayName: `${d.name} (Chipset: ${d.chipset_id})`
        }))
      } catch (err) {
        console.error('Failed to fetch devices:', err)
      } finally {
        this.loadingDevices = false
      }
    },

    openCreateDialog () {
      this.isEditMode = false
      this.editingGroupId = null
      this.formData = { name: '', category_ids: [] }
      this.showFormDialog = true
      this.$nextTick(() => {
        if (this.$refs.groupForm) this.$refs.groupForm.resetValidation()
      })
    },

    openEditDialog (group) {
      this.isEditMode = true
      this.editingGroupId = group.id
      this.formData = {
        name: group.name,
        category_ids: group.categories.map(c => c.id)
      }
      this.showFormDialog = true
      this.$nextTick(() => {
        if (this.$refs.groupForm) this.$refs.groupForm.resetValidation()
      })
    },

    closeFormDialog () {
      this.showFormDialog = false
      this.isEditMode = false
      this.editingGroupId = null
      this.formData = { name: '', category_ids: [] }
    },

    async saveGroup () {
      if (!this.$refs.groupForm.validate()) return

      this.savingGroup = true
      try {
        if (this.isEditMode) {
          await apiService.put(`admin/device-groups/${this.editingGroupId}`, {
            name: this.formData.name,
            category_ids: this.formData.category_ids
          })
          Swal.fire('Success!', 'Device group updated successfully.', 'success')
        } else {
          await apiService.post('admin/device-groups', {
            name: this.formData.name,
            category_ids: this.formData.category_ids
          })
          Swal.fire('Success!', 'Device group created successfully.', 'success')
        }
        this.closeFormDialog()
        await this.fetchGroups()
      } catch (err) {
        const message = err.response?.data?.message || 'An error occurred.'
        Swal.fire('Warning!', message, 'warning')
        console.error(err)
      } finally {
        this.savingGroup = false
      }
    },

    deleteGroup (group) {
      Swal.fire({
        title: 'Are you sure?',
        text: `Delete group "${group.name}"? This will not delete the devices themselves.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        confirmButtonColor: '#d33'
      }).then(async (result) => {
        if (result.value) {
          try {
            await apiService.delete(`admin/device-groups/${group.id}`)
            Swal.fire('Deleted!', 'Device group has been deleted.', 'success')
            await this.fetchGroups()
          } catch (err) {
            const message = err.response?.data?.message || 'Failed to delete group.'
            Swal.fire('Error!', message, 'error')
          }
        }
      })
    },

    closeDialog () {
      this.$emit('close-device-group')
    }
  }
}
</script>
